import { useEffect, useRef, useState } from 'react';
import { FilmIcon, Loader2Icon, Trash2Icon, XIcon } from 'lucide-react';
import { RichTextEditor } from '../RichTextEditor.js';
import type { Lesson } from '@kennisbank/shared';
import { ApiError } from '../../api/client.js';
import {
  createLesson,
  updateLesson,
  deleteLesson,
  mintLessonVideoUploadUrl,
  putWithProgress,
} from './AdminElearningApi.js';
import { clearDraft, readDraft, writeDraft } from './draftStorage.js';

interface LessonDraft {
  name: string;
  content: string;
  videoPath: string | null;
  isLive: boolean;
}

const VIDEO_TYPES = ['video/mp4', 'video/webm'];
const MAX_VIDEO_GB = 2;

/**
 * Les-editor modal. Geport van MoveLogic `LessonEditorModal`, met de
 * `@platform/ui`-velden vervangen door kennisbank-styling. De inhoud is HTML,
 * bewerkt met de gedeelde RichTextEditor; `html` staat aan omdat dit veld per
 * definitie HTML bevat en de editor er dus niet naar hoeft te raden.
 */
export function AdminLessonModal({
  courseId,
  moduleId,
  lesson,
  onClose,
  onSaved,
}: {
  courseId: string;
  moduleId: string;
  lesson: Lesson | null; // null = nieuw
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const draftKey = `lesson.${courseId}.${moduleId}.${lesson?.id ?? 'new'}`;
  const [draft] = useState(() => readDraft<LessonDraft>(draftKey));
  const [restored] = useState(draft !== null);
  const [name, setName] = useState(draft?.name ?? lesson?.name ?? '');
  const [content, setContent] = useState(draft?.content ?? lesson?.content ?? '');
  const [videoPath, setVideoPath] = useState<string | null>(
    draft?.videoPath ?? lesson?.videoPath ?? null,
  );
  const [isLive, setIsLive] = useState(draft?.isLive ?? lesson?.isLive ?? true);
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Autosave als concept bij elke wijziging — een misklik of gesloten tab
  // kost zo nooit werk; het concept komt terug bij heropenen van de modal.
  useEffect(() => {
    writeDraft<LessonDraft>(draftKey, { name, content, videoPath, isLive });
  }, [draftKey, name, content, videoPath, isLive]);

  async function handleVideoFile(file: File): Promise<void> {
    setVideoError(null);
    if (!VIDEO_TYPES.includes(file.type)) {
      setVideoError('Alleen MP4 (H.264) of WebM wordt ondersteund.');
      return;
    }
    if (file.size > MAX_VIDEO_GB * 1024 * 1024 * 1024) {
      setVideoError(`Video mag maximaal ${MAX_VIDEO_GB} GB zijn.`);
      return;
    }
    setUploadPct(0);
    try {
      const { uploadUrl, videoPath: newPath } = await mintLessonVideoUploadUrl(courseId, moduleId, {
        mimeType: file.type,
        sizeBytes: file.size,
      });
      await putWithProgress(uploadUrl, file, setUploadPct);
      // ponytail: vervangt de admin een video en annuleert daarna de modal,
      // dan blijft het nieuwe object als wees achter; opruimen kan later met
      // een lifecycle-regel als het oploopt.
      setVideoPath(newPath);
    } catch (err) {
      setVideoError(err instanceof ApiError ? err.message : 'Uploaden mislukt. Probeer opnieuw.');
    } finally {
      setUploadPct(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function handleCancel(): void {
    clearDraft(draftKey);
    onClose();
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      if (lesson) {
        await updateLesson(courseId, moduleId, lesson.id, { name, content, videoPath, isLive });
      } else {
        await createLesson(courseId, moduleId, { name, content, videoPath, isLive });
      }
      clearDraft(draftKey);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!lesson) return;
    if (!window.confirm('Weet je zeker dat je deze les wilt verwijderen?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteLesson(courseId, moduleId, lesson.id);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verwijderen mislukt.');
      setSaving(false);
    }
  }

  // Bewust géén onClick-close op de backdrop: een misklik naast de modal
  // mag ingevoerd werk niet weggooien. Sluiten kan alleen expliciet.
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-brand-100 px-6 py-4">
          <h2 className="text-lg font-bold text-ink-900">
            {lesson ? 'Les bewerken' : 'Nieuwe les'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-700/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="thin-scroll space-y-5 overflow-y-auto px-6 py-5">
          {restored && (
            <p className="rounded-lg bg-brand-50 px-3 py-2 text-xs text-ink-700">
              Je eerder niet-opgeslagen concept is hersteld. Kies Annuleren om het weg te gooien.
            </p>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Naam
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Naam van de les"
              className="w-full rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Inhoud
            </label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Schrijf hier de lesinhoud…"
              minHeight="min-h-[320px]"
              html
            />
            <p className="mt-1 text-xs text-ink-700/60">
              Selecteer tekst voor vet, cursief, lijsten en links.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Video (optioneel)
            </label>
            {videoPath ? (
              <div className="flex items-center justify-between rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2">
                <span className="inline-flex items-center gap-2 text-sm text-ink-900">
                  <FilmIcon size={16} className="text-brand-700" />
                  Video gekoppeld
                </span>
                <button
                  type="button"
                  onClick={() => setVideoPath(null)}
                  disabled={saving || uploadPct !== null}
                  className="text-sm font-medium text-red-700 hover:underline disabled:opacity-50"
                >
                  Verwijderen
                </button>
              </div>
            ) : uploadPct !== null ? (
              <div className="rounded-lg border border-brand-100 px-3 py-2">
                <div className="mb-1 flex items-center justify-between text-xs text-ink-700">
                  <span>Uploaden…</span>
                  <span>{uploadPct}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-brand-50">
                  <div
                    className="h-full rounded-full bg-brand-600 transition-all"
                    style={{ width: `${uploadPct}%` }}
                  />
                </div>
              </div>
            ) : (
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/webm"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleVideoFile(f);
                }}
                className="block w-full text-sm text-ink-700 file:mr-3 file:rounded-full file:border-0 file:bg-brand-50 file:px-4 file:py-1.5 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
              />
            )}
            <p className="mt-1 text-xs text-ink-700/60">
              MP4 (H.264) of WebM, maximaal {MAX_VIDEO_GB} GB. De video verschijnt boven de
              lesinhoud.
            </p>
            {videoError && <p className="mt-1 text-xs text-red-700">{videoError}</p>}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-brand-100 p-3 hover:bg-brand-50/50">
            <input
              type="checkbox"
              checked={isLive}
              onChange={(e) => setIsLive(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-ink-100 text-brand-600 focus:ring-brand-500"
            />
            <span>
              <span className="block text-sm font-medium text-ink-900">Actief</span>
              <span className="block text-xs text-ink-700/60">
                Inactieve lessen zijn niet zichtbaar voor cursisten en tellen niet mee voor de
                voortgang.
              </span>
            </span>
          </label>

          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>

        <div className="flex items-center justify-between border-t border-brand-100 px-6 py-4">
          <div>
            {lesson && (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                <Trash2Icon size={14} /> Verwijderen
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="rounded-full px-4 py-1.5 text-sm text-ink-700 hover:bg-brand-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || uploadPct !== null || !name.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {saving && <Loader2Icon size={14} className="animate-spin" />}
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
